import { classes } from "components/Utility";
import React, { JSXElementConstructor, ReactElement, ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

export interface MDialogAction {
  code?: string;
  icon?: string;
  helpText?: string;
  onClick?: () => void;
  order?: number;
}

export interface MDialogProps {
  id?: string;
  className?: string;
  contentClassName?: string;
  title?: ReactNode;
  actions?: (MDialogAction | string)[]; // pass just a string to use it as a code
  onHide?: () => void;
  // contentActions expects Button elements
  contentActions?:
    | ReactElement<any, string | JSXElementConstructor<any>>
    | ReactElement<any, string | JSXElementConstructor<any>>[];
  children?: ReactNode; // Using withRouter as a child of MDialog will cause this to crash, not sure why
  focusOnMount?: boolean;
  width?: number | string;
  height?: number | string;
  dontSpecifyWidth?: boolean;
  useFlexInsteadOfTransformForCentering?: boolean; // dialog usually centers itself with css transform. this will stop that, so we can center it with flexbox instead. it was causing issues because getClientBoundingRect doesnt calc right if the element is in something transformed with css transform. so our drag and drop wasnt working
  visible?: boolean; // Use this to control if the dialog is shown or hidden. If not provided defaults to true.
  ariaLabel?: string; // Used to pass down accessibility features to DialogContainer
}

class MDialog extends React.Component<MDialogProps> {
  static defaultProps = {
    visible: true,
    onHide: () => {},
  };

  constructor(props: MDialogProps) {
    super(props);

    this.getAction = this.getAction.bind(this);
    this.getGridActions = this.getGridActions.bind(this);
  }

  getAction(a) {
    let action = a;

    if (typeof a === "string") action = { code: a };

    let actionDefaults;
    switch (action.code) {
      case ACTION_CLOSE:
        actionDefaults = {
          icon: "close",
          helpText: "Close",
          order: 9600,
        };
        break;
      case ACTION_APPLY:
        actionDefaults = {
          icon: "done",
          helpText: "Apply",
          order: 9700,
        };
        break;
      case ACTION_CLONE:
        actionDefaults = {
          icon: "queue",
          helpText: "Save as New Item",
          order: 9700,
        };
        break;
      case ACTION_DELETE:
        actionDefaults = {
          icon: "delete_forever",
          helpText: "Save as New Item",
          order: 9700,
        };
        break;
      case ACTION_ADDITIONAL_OPTIONS:
        actionDefaults = {
          icon: "spellcheck",
          helpText: "Additional Options",
          order: 9700,
        };
        break;
      default:
        actionDefaults = {};
        break;
    }

    return { ...actionDefaults, ...action };
  }

  getGridActions() {
    let actions = this.props.actions ? this.props.actions.map((a) => this.getAction(a)) : [];
    // Render actions in the order they were provided (no sorting)
    return actions.map((a, i) => (
      <IconButton key={`action_${i}`} title={a.helpText} onClick={a.onClick}>
        <Icon>{a.icon}</Icon>
      </IconButton>
    ));
  }

  render() {
    const {
      id,
      className,
      contentClassName,
      visible,
      focusOnMount,
      contentActions,
      title,
      width,
      height,
      dontSpecifyWidth,
      children,
      ariaLabel,
      onHide,
      useFlexInsteadOfTransformForCentering,
    } = this.props;
    const dialogWidth = !dontSpecifyWidth ? width || 350 : undefined;
    const paperStyle: React.CSSProperties = {
      ...(dialogWidth ? { width: dialogWidth, maxWidth: "none" } : {}),
      ...(height ? { height } : {}),
    };

    return (
      <Dialog
        open={visible}
        onClose={onHide}
        aria-label={ariaLabel || ""}
        id={id || "card-dialog"}
        className={classes(
          "card-dialog",
          className,
          useFlexInsteadOfTransformForCentering && "mdialog-no-css-transform",
        )}
        PaperProps={{
          style: Object.keys(paperStyle).length > 0 ? paperStyle : undefined,
        }}
        disableAutoFocus={!focusOnMount}
        disableEnforceFocus={!focusOnMount}
      >
        {title && (
          <DialogTitle className="card-dialog-title">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              {title}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{this.getGridActions()}</Box>
            </Box>
          </DialogTitle>
        )}
        {/* 
          Using dividers prop as workaround for MUI bug where DialogContent padding-top is removed when following DialogTitle.
          The dividers are styled to be transparent in theme/index.js.
          See: https://github.com/mui/material-ui/issues/27851
        */}
        <DialogContent
          dividers
          className={`padding-mdialog-content ${contentClassName || ""}`}
          sx={{ display: "flex", flexDirection: "column", fontSize: "14px", padding: "var(--padding-10px)" }}
        >
          {children}
        </DialogContent>
        {contentActions && <DialogActions>{contentActions}</DialogActions>}
      </Dialog>
    );
  }
}

export const ACTION_CLOSE = "ACTION_CLOSE";
export const ACTION_APPLY = "ACTION_APPLY";
export const ACTION_CLONE = "ACTION_CLONE";
export const ACTION_DELETE = "ACTION_DELETE";
export const ACTION_ADDITIONAL_OPTIONS = "ACTION_ADDITIONAL_OPTIONS";

export default MDialog;
